import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { STATES, TIERS } from "@/components/leadUtils";
import {
  ShieldCheck, Target, Flame, TrendingUp, CheckCircle2, ArrowRight,
  MapPin, Users, Lock, Zap,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const differentiators = [
  { Icon: Flame, title: "Verified Buy/Sell Intent", desc: "Every lead is scored on real intent, budget and timeline. No tire-kickers — only people actually buying or selling a home." },
  { Icon: Lock, title: "One Agent Per ZIP", desc: "Own a ZIP code and every lead in it is yours alone. Unlike Zillow, we never sell the same lead to five competing agents." },
  { Icon: Target, title: "Priced by Population", desc: "Pay only for the territory you want — $25, $50 or $100/month per ZIP based on population across California, Arizona and Nevada." },
];

const stats = [
  { value: "94%", label: "Leads with verified buy/sell intent" },
  { value: "1", label: "Agent per ZIP — always exclusive" },
  { value: "3", label: "High-growth launch states" },
  { value: "2.5%", label: "Avg. commission per closed lead" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#080C14] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grain-overlay pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-mono text-xs uppercase tracking-widest px-3 py-1">
              <Zap className="h-3 w-3 mr-1" /> Premier Agent Leads · Reinvented
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
              Leads that actually
              <span className="text-emerald-400"> buy and sell homes.</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-xl">
              EstateLead Premier delivers exclusive, intent-scored seller and buyer leads
              across California, Arizona and Nevada. Own your ZIP codes — so every dollar you spend
              turns into real closings, not dead ends.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="hero-agent-cta">
                <Button className="w-full sm:w-auto bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-8 h-12 text-base gap-2">
                  Get Premier Leads <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/get-started" data-testid="hero-seller-cta">
                <Button variant="outline" className="w-full sm:w-auto border-slate-700 text-white hover:bg-slate-800 rounded-full px-8 h-12 text-base bg-transparent">
                  I'm Buying / Selling
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> No shared leads</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Cancel anytime</span>
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-4 bg-emerald-500/10 blur-3xl rounded-full" />
            <img
              src={HERO_IMG}
              alt="Luxury modern home"
              className="relative rounded-2xl border border-slate-800 shadow-2xl w-full object-cover h-[420px]"
            />
            <Card className="absolute -bottom-6 -left-6 bg-slate-900/90 backdrop-blur-xl border-emerald-500/30 p-4 glow-emerald hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
                <div>
                  <div className="text-xs font-mono text-slate-400 uppercase tracking-wider">New Hot Lead</div>
                  <div className="text-sm font-semibold text-white">Seller · Scottsdale, AZ · $1.2M</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-800/80 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-emerald-400">{s.value}</div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 font-semibold">Why we beat the rest</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">
            Better than Zillow, Market Leader & RedX
          </h2>
          <p className="text-slate-400 mt-4">
            The old lead platforms flood you with cold, recycled contacts. We built EstateLead
            around a single promise: worthwhile leads that convert.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {differentiators.map(({ Icon, title, desc }) => (
            <Card key={title} className="bg-slate-900/80 border-slate-800 hover:border-emerald-500/40 transition-all p-8 rounded-xl group">
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Icon className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">{title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Markets */}
      <section className="bg-slate-950/50 border-y border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-emerald-400" />
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 font-semibold">Launch Markets</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-10">Own your territory</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {STATES.map((s) => (
              <Card key={s.code} className="bg-slate-900/80 border-slate-800 p-6 rounded-xl hover:border-emerald-500/40 transition-all">
                <div className="font-mono text-emerald-400 text-sm">{s.code}</div>
                <div className="font-heading text-lg font-semibold mt-1">{s.name}</div>
                <div className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> High-growth market
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing / CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Card className="bg-gradient-to-br from-slate-900 to-slate-950 border-emerald-500/30 rounded-2xl p-8 sm:p-14 glow-emerald text-center relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay" />
          <Badge className="mb-5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-mono text-xs uppercase tracking-widest">
            <ShieldCheck className="h-3 w-3 mr-1" /> Premier Membership
          </Badge>
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight mb-4">
            Own a ZIP from $25<span className="text-xl text-slate-500 font-normal">/month</span>
          </h2>
          <p className="text-slate-400 max-w-lg mx-auto mb-8">
            Subscribe to ZIP codes and receive every lead in them — exclusively. No per-lead fees. Cancel anytime.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-10 relative" data-testid="pricing-tiers">
            {Object.entries(TIERS).map(([k, t]) => (
              <div key={k} className="rounded-xl border border-slate-800 bg-slate-950/60 p-5" data-testid={`pricing-tier-${k}`}>
                <div className="font-mono text-xs uppercase tracking-widest text-slate-400">{t.label} people</div>
                <div className="font-heading text-3xl font-extrabold text-emerald-400 mt-2">${t.price}<span className="text-sm text-slate-500 font-normal">/mo</span></div>
              </div>
            ))}
          </div>
          <Link to="/register">
            <Button className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-10 h-12 text-base gap-2">
              Start Getting Leads <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-slate-500">
            <Users className="h-4 w-4" /> Exclusive ZIP territories across 3 states
          </div>
        </Card>
      </section>

      <footer className="border-t border-slate-800/80 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <span>© 2026 EstateLead Premier. All rights reserved.</span>
          <span className="font-mono text-xs">Exclusive ZIP territories · CA · AZ · NV</span>
        </div>
      </footer>
    </div>
  );
}
